import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '@/components/SEO';
import StructuredData from '@/components/StructuredData';
import HeroSection from '@/components/HeroSection';
import SectionHeading from '@/components/SectionHeading';
import FeatureCard from '@/components/FeatureCard';
import CTASection from '@/components/CTASection';
import BlogCard from '@/components/BlogCard';
import { Shield, Server, FileCheck, Clock, Users, TrendingUp } from 'lucide-react';

const Home = () => {
  const [solutions, setSolutions] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);

  useEffect(() => {
    // Load solutions
    fetch(`${process.env.PUBLIC_URL}/data/solutions.json`)
      .then(res => res.json())
      .then(data => setSolutions(data.slice(0, 6)))
      .catch(err => console.error('Error loading solutions:', err));

    // Load blog posts
    // fetch('/data/blog.json')
    //   .then(res => res.json())
    //   .then(data => setBlogPosts(data.slice(0, 3)))
    //   .catch(err => console.error('Error loading blog posts:', err));
  }, []);

  // const expertise = [
  //   {
  //     icon: Shield,
  //     title: '24/7 SOC Monitoring',
  //     description: 'Round-the-clock security operations center with expert analysts protecting your digital assets.',
  //     link: '/services'
  //   },
  //   {
  //     icon: FileCheck,
  //     title: 'Compliance Expertise',
  //     description: 'Navigate SWIFT CSP, GDPR, PCI DSS, and Saudi PDPL with our specialized compliance advisory.',
  //     link: '/advisory'
  //   },
  //   {
  //     icon: Server,
  //     title: 'PAM & IAM Solutions',
  //     description: 'Privileged Access Management and Identity solutions for Zero Trust implementation.',
  //     link: '/solutions#pam'
  //   },
  //   {
  //     icon: Server,
  //     title: 'Cyber Threat Intelligence and Brand Monitoring',
  //     description: 'Get real-time visibility into the Darkweb, Cyber Intelligence and',
  //     link: '/solutions#pam'
  //   }
  // ];

  const whyUs = [
    {
      icon: Clock,
      title: '24/7 Support',
      description: 'Round-the-clock monitoring and incident response'
    },
    {
      icon: Users,
      title: 'Expert Team',
      description: 'Certified security professionals with deep Middle Eastern expertise'
    },
    {
      icon: TrendingUp,
      title: 'Proven Results',
      description: 'Trusted by leading organizations across the region'
    }
  ];

  return (
    <>
      <SEO
        title="Cybersecurity Solutions in Saudi Arabia | Cybersecurity Services in KSA | Gulf Digital"
        description="Gulf Digital delivers industry-leading Cybersecurity solutions in Saudi Arabia and Cybersecurity services in KSA. We help businesses strengthen their security posture with SOC, PAM, IAM, cloud security, cyber threat intelligence, DDoS protection, and managed cybersecurity services."
        keywords="Cybersecurity solutions in Saudi Arabia, Cybersecurity services in Saudi Arabia, Cybersecurity solutions in KSA, Cybersecurity services in KSA, cybersecurity, Gulf Digital, SOC, Security Operations Center, DDoS protection, Dark Web Monitoring, IAM, Identity and Access Management, PAM, Privileged Access Management, CIAM, Cloud Security, Cyber Threat Intelligence, Managed Security Services, Operational Technology Security, Zero Trust Security"
        ogTitle="Cybersecurity Solutions in Saudi Arabia | Gulf Digital"
        ogDescription="Protect your business with advanced Cybersecurity solutions in Saudi Arabia and trusted Cybersecurity services in KSA. Gulf Digital offers end-to-end cybersecurity solutions including PAM, IAM, SOC, cloud security, and threat intelligence."
        twitterTitle="Cybersecurity Solutions in Saudi Arabia | Cybersecurity Services in KSA"
        twitterDescription="Discover enterprise-grade Cybersecurity solutions in Saudi Arabia and Cybersecurity services in KSA. Gulf Digital helps organizations secure digital assets with PAM, IAM, SOC, cloud security, and managed cybersecurity solutions."
      />
      <StructuredData type="Organization" />

      {/* Hero Section */}
      <HeroSection
        eyebrow="Cybersecurity Excellence in Middle East"
        title="Protecting Your Digital Future with Advanced Security Solutions"
        subtitle="Gulf Digital is your trusted Value Added Distributor for enterprise cybersecurity solutions, managed services, and compliance advisory across the Middle East."
        primaryCTA={{ text: 'Contact Sales', href: '/contact' }}
        secondaryCTA={{ text: 'Explore Solutions', href: '/solutions' }}
        showTrustStrip={true}
      />

      {/* Quick Links Section */}
      <section className="py-16 bg-white" data-testid="quick-links-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link 
              to="/solutions" 
              className="group p-8 rounded-2xl bg-gradient-to-br from-brand-maroon to-brand-maroon-dark text-white hover:shadow-soft-lg transition-all duration-300"
              data-testid="quick-link-solutions"
            >
              <Shield className="w-10 h-10 mb-4" />
              <h3 className="text-2xl font-bold mb-2 text-white/90">Solutions</h3>
              <p className="text-white/90">Comprehensive security solutions for every need</p>
            </Link>
            
            <Link 
              to="/services" 
              className="group p-8 rounded-2xl bg-gradient-to-br from-warm-gray-800 to-warm-gray-900 text-white hover:shadow-soft-lg transition-all duration-300"
              data-testid="quick-link-services"
            >
              <Server className="w-10 h-10 mb-4" />
              <h3 className="text-2xl font-bold mb-2 text-white/90">Services</h3>
              <p className="text-white/90">Managed security and advisory services</p>
            </Link>
            
            <Link 
              to="/advisory" 
              className="group p-8 rounded-2xl bg-gradient-to-br from-warm-gray-600 to-warm-gray-700 text-white hover:shadow-soft-lg transition-all duration-300"
              data-testid="quick-link-advisory"
            >
              <FileCheck className="w-10 h-10 mb-4" />
              <h3 className="text-2xl font-bold mb-2 text-white/90">Advisory</h3>
              <p className="text-white/90">Compliance and regulatory guidance</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Our Expertise Section */}
      {/* <section className="py-20 bg-warm-gray-50" data-testid="expertise-section">
        <div className="container-custom">
          <SectionHeading
            eyebrow="Our Expertise"
            title="Comprehensive Cybersecurity Solutions"
            description="From threat detection to compliance, we provide end-to-end security solutions tailored for the Middle East region."
            titleColor='foreground'
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {expertise.map((item, index) => (
              <FeatureCard
                key={index}
                icon={item.icon}
                title={item.title}
                description={item.description}
                link={item.link}
                delay={index * 0.1}
              />
            ))}
          </div>
        </div>
      </section> */}

      {/* Featured Solutions */}
      {solutions.length > 0 && (
        <section className="py-20 bg-white" data-testid="featured-solutions-section">
          <div className="container-custom">
            <SectionHeading
              eyebrow="Featured Solutions"
              title="Enterprise-Grade Security Solutions"
              titleColor='foreground'
              description="Discover our comprehensive portfolio of cybersecurity solutions designed to protect your organization."
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {solutions.map((solution, index) => (
                <FeatureCard
                  key={solution.id}
                  title={solution.title}
                  description={solution.description}
                  features={solution.features.slice(0, 3)}
                  link="/solutions"
                  delay={index * 0.1}
                />
              ))}
            </div>
            <div className="text-center mt-12">
              <Link 
                to="/solutions" 
                className="inline-flex items-center text-brand-maroon font-semibold hover:underline"
              >
                View all solutions →
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Why Gulf Digital */}
      <section className="py-20 bg-warm-gray-50" data-testid="why-us-section">
        <div className="container-custom">
          <SectionHeading
            eyebrow="Why Gulf Digital"
            title="Your Trusted Cybersecurity Distributer"
            description="We combine deep technical expertise with regional knowledge to deliver exceptional security outcomes."
            titleColor='foreground'
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {whyUs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-brand-maroon/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-brand-maroon" />
                </div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Blog Posts */}
      {/* {blogPosts.length > 0 && (
        <section className="py-20 bg-white" data-testid="blog-section">
          <div className="container-custom">
            <SectionHeading
              eyebrow="From the Blog"
              title="Latest Insights & News"
              description="Stay informed with the latest cybersecurity trends, best practices, and industry updates."
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {blogPosts.map((post, index) => (
                <BlogCard key={post.id} post={post} delay={index * 0.1} />
              ))}
            </div>
          </div>
        </section>
      )} */}

      {/* CTA Section */}
      <CTASection />
    </>
  );
};

export default Home;